/**
 * @file src/ui/react/regions/DeckHolders.tsx
 * @description Who is allowed to drive THIS deck, as a small stack of faces on the deck itself.
 *
 * With several orbiters on screen and several people in the room, nothing on a deck said who may
 * play it — you had to infer it from who happened to be moving something, which only tells you
 * about the one control they touched, and only while they are touching it.
 *
 * This is a GRANT, and the badge on a control is a LEASE. They look alike and mean opposite things:
 * the badge says "this person has their hand on this knob right now" and is gone a moment later;
 * this says "this person may drive this deck at all", whether or not anyone is touching anything.
 * The same face appearing in both places is the two answers agreeing, not a duplicate.
 *
 * A deck is a DECK whether or not a card is on it, so the faces show on an empty one too — that is
 * the deck you are about to load, and who may load it is the same question. So the faces are keyed
 * on the DECK (its wire id), and a filled tile simply resolves its own voice to that key. The two
 * entry points differ only in what they know: inside a tile, the voice comes from the engine
 * context, like the focused-tile marker; the empty-stage placeholder already knows its index.
 * Either way it is pointer-transparent and never blocks the deck. A single orbiter has no decks
 * and no `voiceId`, so it renders nothing at all.
 *
 * Grants belong to a SECTION of the score and the score advances, so this follows the room's
 * derived grants rather than reading them once: `roomStore.holdersOfWire` answers for right now,
 * and both the store and the score announce when that changes.
 */
import { useEffect, useState } from 'react';
import { useEngine } from '../../../react/engine/EngineContext';
import { RoomMemberAvatar } from '../../../room/RoomMemberAvatar';
import { roomScore } from '../../../room/roomScore.js';
import { roomStore } from '../../../room/roomStore.js';
import { getT } from '../../../i18n/index.js';

/**
 * Four faces, and past that the fourth slot becomes a counter — the rule the score header's own
 * stack already follows, so the same room of people folds the same way wherever it is shown.
 */
const MAX_FACES = 4;

export interface DeckHolderFacesProps {
  /** The deck, when the caller knows it directly — the empty stage knows its own index. */
  wireId?: string | null;
  /** The voice on the deck, when the caller is the tile showing it. */
  voiceId?: string | null;
}

/**
 * Who may drive this deck, in room order. Empty is a real answer.
 *
 * The voice is resolved to its deck HERE rather than by the caller, so a rearranged room (which
 * re-seeds that mapping) is answered on the next notification instead of from a stale key.
 */
function holdersOfDeck({ wireId, voiceId }: DeckHolderFacesProps): string[] {
  const deck = voiceId != null ? roomStore.wireIdForVoice(voiceId) : wireId ?? null;
  if (deck === null) return [];
  return roomStore.holdersOfWire(deck);
}

function sameHolders(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((clientId, index) => clientId === b[index]);
}

export function DeckHolderFaces({ wireId = null, voiceId = null }: DeckHolderFacesProps) {
  const [holders, setHolders] = useState<string[]>(() => holdersOfDeck({ wireId, voiceId }));

  useEffect(() => {
    // Re-seed on (re)subscribe: the same component instance can be reused for a different deck
    // when tiles are rearranged, and the room may not change again for a long time.
    const read = () => setHolders((prev) => {
      const next = holdersOfDeck({ wireId, voiceId });
      // Same array back when nothing moved — the score announces on every displayed second, and a
      // new array each time would re-render this stack under every tile once a second.
      return sameHolders(prev, next) ? prev : next;
    });
    read();
    const offStore = roomStore.subscribe(read);
    const offScore = roomScore.subscribe(read);
    return () => {
      offStore();
      offScore();
    };
  }, [voiceId, wireId]);

  if (holders.length === 0) return null;

  const overflow = holders.length > MAX_FACES ? holders.length - (MAX_FACES - 1) : 0;
  const shown = overflow ? holders.slice(0, MAX_FACES - 1) : holders;

  return (
    <div
      className="orbiters-react-ui__deck-holders"
      data-ui-react-region="deck-holders"
      role="group"
      aria-label={getT()('room.deck.holders')}
    >
      {shown.map((clientId) => (
        <RoomMemberAvatar
          key={clientId}
          clientId={clientId}
          // A share of the TILE, not a pixel count: the same box every other control on this tile
          // sizes itself against, so the faces shrink with the deck they belong to.
          size="var(--orb-deck-face)"
          className="orbiters-react-ui__deck-holder"
          // Nothing else here says who these people are — no name beside them, no row to read them
          // off — so each face carries its own, on hover and to a screen reader.
          named
        />
      ))}
      {overflow ? (
        <span className="orbiters-react-ui__deck-holders-more">+{overflow}</span>
      ) : null}
    </div>
  );
}

/**
 * The play-UI region: the faces on the deck THIS tile is showing. The tile knows its voice and
 * nothing else about the room, so the voice is all it passes; an empty deck has no tile and mounts
 * {@link DeckHolderFaces} with its own index instead.
 */
export function DeckHolders() {
  const { voiceId } = useEngine();
  return <DeckHolderFaces voiceId={voiceId} />;
}
